import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Activity, BarChart3 } from 'lucide-react';
import { cn, formatNumber, getChangeColor } from '../utils';
import { FuturesData } from '../services/marketData';

interface FuturesCarouselProps {
  futuresData: FuturesData[];
}

export const FuturesCarousel: React.FC<FuturesCarouselProps> = ({ futuresData }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying || futuresData.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === futuresData.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, futuresData.length]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex(currentIndex === 0 ? futuresData.length - 1 : currentIndex - 1);
    setTimeout(() => setIsAutoPlaying(true), 10000); // Resume auto-play after 10 seconds
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex(currentIndex === futuresData.length - 1 ? 0 : currentIndex + 1);
    setTimeout(() => setIsAutoPlaying(true), 10000); // Resume auto-play after 10 seconds
  };

  const goToSlide = (index: number) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
    setTimeout(() => setIsAutoPlaying(true), 10000); // Resume auto-play after 10 seconds
  };

  if (futuresData.length === 0) {
    return (
      <div className="glass-card p-6">
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <Activity className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-400">Loading futures data...</p>
          </div>
        </div>
      </div>
    );
  }

  const currentFuture = futuresData[currentIndex];
  const isPositive = currentFuture.change > 0;
  const Icon = isPositive ? TrendingUp : TrendingDown;
  const sentimentColor = currentFuture.sentiment === 'Bullish' ? 'text-green-400' : 
                        currentFuture.sentiment === 'Bearish' ? 'text-red-400' : 'text-yellow-400';

  return (
    <div className="glass-card p-6 relative overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-gray-400 text-sm font-medium flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-blue-400" />
          Market Sentiment
        </h3>
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-1.5 h-1.5 rounded-full",
            isAutoPlaying ? "bg-green-400 animate-pulse" : "bg-gray-400"
          )} />
        </div>
      </div>

      {/* Main Content */}
      <div className="relative flex-1 flex flex-col justify-center">
        <div className="text-center">
          {/* Symbol */}
          <div className="mb-3">
            <div className={cn(
              "w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold text-sm",
              isPositive ? "bg-green-500/20 border border-green-500/40" : "bg-red-500/20 border border-red-500/40"
            )}>
              {currentFuture.symbol.substring(0, 3)}
            </div>
            <h3 className="text-lg font-bold text-white">{currentFuture.name}</h3>
            <p className="text-xs text-gray-400">{currentFuture.symbol}</p>
          </div>

          {/* Price and Change */}
          <div className="mb-3">
            <p className="text-xl font-bold text-white mb-1">
              {currentFuture.symbol.includes('JPY') ? 
                currentFuture.price.toFixed(2) : 
                currentFuture.symbol.includes('DXY') ? 
                currentFuture.price.toFixed(2) :
                currentFuture.price < 10 ? 
                currentFuture.price.toFixed(4) : 
                formatNumber.currency(currentFuture.price)
              }
            </p>
            <div className={cn(
              "flex items-center justify-center gap-1 text-sm font-medium",
              getChangeColor(currentFuture.change)
            )}>
              <Icon className="w-4 h-4" />
              <span>
                {currentFuture.change > 0 ? '+' : ''}{currentFuture.change.toFixed(4)}
              </span>
              <span>({formatNumber.percentage(Math.abs(currentFuture.changePercent))})</span>
            </div>
          </div>

          {/* Sentiment */}
          <div className="flex items-center justify-center gap-3 text-xs">
            <div className="text-center">
              <p className="text-gray-400 mb-1">Sentiment</p>
              <p className={cn("font-bold", sentimentColor)}>
                {currentFuture.sentiment}
              </p>
            </div>
            {currentFuture.volume && (
              <div className="text-center">
                <p className="text-gray-400 mb-1">Volume</p>
                <p className="font-bold text-white">
                  {formatNumber.compact(currentFuture.volume)}
                </p>
              </div>
            )}
            <div className="text-center">
              <p className="text-gray-400 mb-1">Updated</p>
              <p className="font-bold text-white">
                {new Date(currentFuture.lastUpdated).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="flex items-center justify-between mt-auto pt-4">
        <button
          onClick={goToPrevious}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Dots Indicator */}
        <div className="flex items-center gap-2">
          {futuresData.slice(0, 8).map((_, index) => ( // Show max 8 dots
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-200",
                index === currentIndex ? "bg-blue-400 w-6" : "bg-white/30 hover:bg-white/50"
              )}
            />
          ))}
        </div>

        <button
          onClick={goToNext}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Auto-play indicator */}
      <div className="absolute top-4 right-4">
        <div className={cn(
          "text-xs px-2 py-1 rounded-full",
          isAutoPlaying ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"
        )}>
          {isAutoPlaying ? 'Auto' : 'Manual'}
        </div>
      </div>
    </div>
  );
};

export default FuturesCarousel;